import { LoginIcon } from '@heroicons/react/outline';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { type KeyboardEvent, useId, useMemo, useRef } from 'react';

import clsxm from '@/lib/clsxm';
import { useFavoriteTools } from '@/hooks/useFavoriteTools';

import { SimpleTooltip } from '@/components/ui/tooltip';

import { useUser } from '@/utils/useUser';

import { AccountDropdown } from './AccountMenu';
import { CreditsRing } from './Credits';
import {
  activeHref,
  APP_ITEMS,
  type NavItem,
  pinnedItems,
  PRIMARY_ITEMS,
  TOOLS_ITEM,
} from './navModel';
import { nav, useNavTrack } from './navUi';
import WhatsNewBell from './WhatsNewBell';
import ToolsFlyout, { useToolsFlyout } from '../ToolsFlyout';

const iconLink = (active: boolean) =>
  clsxm(
    'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors',
    active
      ? nav.active
      : 'text-[#6f6f86] hover:bg-[#f4f4fb] hover:text-[#10101c] dark:text-slate-400 dark:hover:bg-white/[0.06] dark:hover:text-white',
    nav.focus,
  );

/**
 * The 80 px icon rail of NarrowDashboardLayout (/ai-images, /ai-videos, the
 * editors): the sidebar's items as icons with tooltips, the Tools flyout on
 * hover, and What's New, credits and the account menu at the bottom.
 */
export default function NavRail({ className }: { className?: string }) {
  const { t } = useLocale('dashboard');
  const { asPath } = useRouter();
  const track = useNavTrack('rail');
  const { user, isLoading } = useUser();
  const signedIn = !!user || isLoading;
  const { favoriteTools, isLoading: favoritesLoading } = useFavoriteTools();

  const pins = useMemo(
    () => (user ? pinnedItems(favoriteTools) : []),
    [user, favoriteTools],
  );
  const primary = signedIn ? PRIMARY_ITEMS : [TOOLS_ITEM];
  const listed = signedIn ? pins : APP_ITEMS;
  const current = activeHref(
    asPath,
    [...primary, ...listed].map((item) => item.href),
  );

  const icon = (item: NavItem, section: string) => {
    const active = current === item.href;
    const label = item.translate === false ? item.name : t(item.name);
    // Icons only here, so a New tag goes into the tooltip.
    const tip = item.badge === 'new' ? `${label} · ${t('New')}` : label;
    return (
      <li key={item.slug}>
        <SimpleTooltip title={tip} side='right'>
          <Link
            href={item.href}
            aria-label={label}
            aria-current={active ? 'page' : undefined}
            onClick={() => track(section, item.href)}
            className={iconLink(active)}
          >
            <item.icon aria-hidden='true' className='h-5 w-5' />
          </Link>
        </SimpleTooltip>
      </li>
    );
  };

  return (
    <div
      data-nav-panel=''
      className={clsxm(
        'flex h-full w-20 flex-col items-center border-e',
        nav.panel,
        nav.hairline,
        className,
      )}
    >
      <Link
        href={user ? '/dashboard' : '/'}
        aria-label='Easy-Peasy.AI'
        onClick={() => track('logo', user ? '/dashboard' : '/')}
        className={clsxm(
          'mt-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
          nav.focus,
        )}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src='/images/marky-chat.png'
          alt=''
          width={34}
          height={28}
          className='h-7 w-auto'
        />
      </Link>

      <nav
        aria-label={t('Menu')}
        className='scrollbar-hide mt-2 flex min-h-0 w-full flex-1 flex-col items-center overflow-y-auto pb-2'
      >
        <ul className='flex flex-col items-center gap-1'>
          {primary.map((item) =>
            item === TOOLS_ITEM ? (
              <RailTools
                key={item.slug}
                item={item}
                active={current === item.href}
                onClick={() => track('primary', item.href)}
              />
            ) : (
              icon(item, 'primary')
            ),
          )}
        </ul>
        <span
          aria-hidden='true'
          className='my-2 h-px w-6 shrink-0 bg-[#0f0f28]/[0.08] dark:bg-white/10'
        />
        {signedIn && (!user || favoritesLoading) ? (
          <div aria-hidden='true' className='flex flex-col items-center gap-1'>
            {[0, 1, 2].map((index) => (
              <span
                key={index}
                className='m-2.5 h-5 w-5 animate-pulse rounded-md bg-[#0f0f28]/[0.06] dark:bg-white/10'
              />
            ))}
          </div>
        ) : (
          <ul
            aria-label={signedIn ? t('Pinned') : t('Apps')}
            className='flex flex-col items-center gap-1'
          >
            {listed.map((item) => icon(item, signedIn ? 'pinned' : 'apps'))}
          </ul>
        )}
      </nav>

      <div
        className={clsxm(
          'flex w-full shrink-0 flex-col items-center gap-1 border-t py-3',
          nav.hairline,
        )}
      >
        {signedIn && <WhatsNewBell variant='rail' />}
        <CreditsRing />
        {user ? (
          <AccountDropdown variant='rail' />
        ) : isLoading ? (
          <span
            aria-hidden='true'
            className='m-1 h-8 w-8 animate-pulse rounded-full bg-[#0f0f28]/[0.07] dark:bg-white/10'
          />
        ) : (
          <SimpleTooltip title={t('Login')} side='right'>
            <Link
              href='/login'
              aria-label={t('Login')}
              onClick={() => track('auth', '/login')}
              className={iconLink(false)}
            >
              <LoginIcon
                aria-hidden='true'
                className='h-5 w-5 rtl:rotate-180'
              />
            </Link>
          </SimpleTooltip>
        )}
      </div>
    </div>
  );
}

/**
 * Tools in the rail: hovering opens the flyout, and so does the arrow key
 * pointing away from the rail, which also moves focus into it. No tooltip:
 * it would sit on top of the flyout.
 */
function RailTools({
  item,
  active,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  onClick: () => void;
}) {
  const { t } = useLocale('dashboard');
  const flyout = useToolsFlyout();
  const linkRef = useRef<HTMLAnchorElement>(null);
  const id = useId();
  const label = t(item.name);

  const onKeyDown = (event: KeyboardEvent) => {
    const rtl = document.documentElement.dir === 'rtl';
    if (event.key === (rtl ? 'ArrowLeft' : 'ArrowRight') && linkRef.current) {
      event.preventDefault();
      if (!flyout.open) flyout.toggle(linkRef.current);
    }
  };

  return (
    <li
      onMouseEnter={() => linkRef.current && flyout.hoverIn(linkRef.current)}
      onMouseLeave={flyout.hoverOut}
    >
      <Link
        ref={linkRef}
        href={item.href}
        aria-label={label}
        aria-current={active ? 'page' : undefined}
        aria-expanded={flyout.open}
        aria-controls={flyout.open ? id : undefined}
        onClick={onClick}
        onKeyDown={onKeyDown}
        className={iconLink(active)}
      >
        <item.icon aria-hidden='true' className='h-5 w-5' />
      </Link>
      {flyout.open && flyout.anchor && (
        <ToolsFlyout
          id={id}
          anchor={flyout.anchor}
          variant='rail'
          focusOnOpen={flyout.focusOnOpen}
          onMouseEnter={flyout.keep}
          onMouseLeave={flyout.hoverOut}
          onClose={(returnFocus) => {
            flyout.close();
            if (returnFocus) linkRef.current?.focus();
          }}
        />
      )}
    </li>
  );
}
